import { Box }             from '@chakra-ui/react';
import { Grid }            from '@chakra-ui/react';
import { GridItem }        from '@chakra-ui/react';
import { Tabs }            from '@chakra-ui/react';
import { Text }            from '@chakra-ui/react';
import { Control }         from '@/components/ui/rich-text-editor';
import { RichTextEditor }  from '@/components/ui/rich-text-editor';
import { Editor }          from '@tiptap/react';
import { useTranslation }  from 'react-i18next';

type LangEditors = { excerpt: Editor | null; content: Editor | null };

interface IGeneralInformationSectionProps {
	editors: {
		fr: LangEditors;
		en: LangEditors;
		jp: LangEditors;
	};
	standTitle: string;
	corporateName: string;
	isDisabled?: boolean;
	hall?: string;
	slot?: string;
}

const TabContent = ({ editors, isDisabled }: { editors: LangEditors; isDisabled?: boolean }) => {
	const { t } = useTranslation();
	return (
		<>
			<Box pt="20px" pb="15px">
				<Text className='poppins-semibold'>
					{t('pages.editStand.form.generalInformation.stand.excerpt.title')}
				</Text>
			</Box>
			<RichTextEditor.Root editor={editors.excerpt} css={{ "--content-min-height": "80px" }} disabled={isDisabled}>
				<RichTextEditor.Toolbar>
					<RichTextEditor.ControlGroup>
						<Control.Bold />
						<Control.Italic />
						<Control.Underline />
					</RichTextEditor.ControlGroup>
				</RichTextEditor.Toolbar>
				<RichTextEditor.Content />
			</RichTextEditor.Root>
			<Box pt="30px" pb="15px">
				<Text className='poppins-semibold'>
					{t('pages.editStand.form.generalInformation.stand.content.title')}
				</Text>
			</Box>
			<RichTextEditor.Root editor={editors.content} css={{ "--content-min-height": "200px" }} disabled={isDisabled}>
				<RichTextEditor.Toolbar>
					<RichTextEditor.ControlGroup>
						<Control.Bold />
						<Control.Italic />
						<Control.Underline />
						<Control.BulletList />
					</RichTextEditor.ControlGroup>
				</RichTextEditor.Toolbar>
				<RichTextEditor.Content />
			</RichTextEditor.Root>
		</>
	);
};

const GeneralInformationSection = ({ editors, standTitle, corporateName, isDisabled, hall, slot }: IGeneralInformationSectionProps) => {
	const { t } = useTranslation();

	const tabTriggerProps = {
		paddingInlineStart: '8px',
		paddingInlineEnd: '8px',
		_selected: { borderBottomWidth: '2px', borderBottomColor: 'black' },
	};

	return (
		<>
			<Text className='poppins-bold' textStyle="lg" pb="20px">
				{t('pages.editStand.form.generalInformation.title')}
			</Text>
			<Grid templateColumns="repeat(2, 1fr)">
				<GridItem colSpan={1}>
					<Box display="flex" flexDirection="column" gap="12px">
						<Text textStyle="xs">{t('pages.editStand.form.generalInformation.stand.title')}</Text>
						<Text className='poppins-semibold'>{standTitle}</Text>
					</Box>
				</GridItem>
				<GridItem colSpan={1}>
					<Box display="flex" flexDirection="column" gap="12px">
						<Text textStyle="xs">{t('pages.editStand.form.generalInformation.stand.legalEntity')}</Text>
						<Text className='poppins-semibold'>{corporateName}</Text>
					</Box>
				</GridItem>
			</Grid>
			{
				(!!hall || !!slot) &&
				<Grid templateColumns="repeat(2, 1fr)" mt="30px">
					{
						!!hall &&
						<GridItem colSpan={1} style={{ display: 'flex', gap: "15px" }}>
							<Box display="flex" flexDirection="column" gap="12px">
								<Text textStyle="xs">{t('pages.stands.list.hall')}</Text>
								<Text className='poppins-semibold'>{hall}</Text>
							</Box>
							{
								!!slot &&
								<Box display="flex" flexDirection="column" gap="12px">
									<Text textStyle="xs">{t('pages.stands.list.slot')}</Text>
									<Text className='poppins-semibold'>{slot}</Text>
								</Box>
							}
						</GridItem>
					}
				</Grid>
			}
			<Tabs.Root defaultValue="fr" mt="30px">
				<Tabs.List>
					<Tabs.Trigger value="fr" {...tabTriggerProps}>
						<Text className='poppins-semibold'>{t('app.languages.fr')}</Text>
					</Tabs.Trigger>
					<Tabs.Trigger value="en" {...tabTriggerProps}>
						<Text className='poppins-semibold'>{t('app.languages.en')}</Text>
					</Tabs.Trigger>
					<Tabs.Trigger value="jp" {...tabTriggerProps}>
						<Text className='poppins-semibold'>{t('app.languages.jp')}</Text>
					</Tabs.Trigger>
				</Tabs.List>
				<Tabs.Content value="fr"><TabContent editors={editors.fr} isDisabled={isDisabled} /></Tabs.Content>
				<Tabs.Content value="en"><TabContent editors={editors.en} isDisabled={isDisabled} /></Tabs.Content>
				<Tabs.Content value="jp"><TabContent editors={editors.jp} isDisabled={isDisabled} /></Tabs.Content>
			</Tabs.Root>
		</>
	);
};

export default GeneralInformationSection;
